import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { DrawerService } from '../../../../core/services/drawer.service';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NoDataComponent } from '../../../../pages/content/no-data/no-data.component';
import { CreateBipCaseComponent } from '../create-bip-case/create-bip-case.component';
import { InsightsService } from '../../insights.service';
import { finalize, Observable, Subject, takeUntil } from 'rxjs';
import { DrawerHeaderComponent } from '../../../../components/drawer-header/drawer-header.component';
import {
  ApiResponse,
  BIP,
  CaseDetailModel,
  ViewBipCaseDrawerParams,
} from '../../insights.interface';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { BehaviorStatus, InsightMode } from '../../insights.enums';
import { LoaderService } from '../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { DefineBehaviorComponent } from './define-behavior/define-behavior.component';
import _ from 'lodash';
import { BehaviorListComponent } from './behavior-list/behavior-list.component';
import { ManageStudentTeamComponent } from '../manage-student-team/manage-student-team.component';
import { InsightsAdminComponent } from '../insights-admin/insights-admin.component';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { StudentImageComponent } from '../../student-image/student-image.component';
import { CommonService } from '../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../core/models/error.models';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { AddDataComponent } from './add-data/add-data.component';
import { SupportComponent } from '../../../support/support.component';
import { DataLogComponent } from './data-log/data-log.component';
import { PrintQuickGuideComponent } from './quick-guide/print-quick-guide/print-quick-guide.component';
import { OrganizationService } from '../../../organizations/organization.service';
import {
  ApiStratergyResponses,
  BehaviorCase,
  TransformedData,
} from './quick-guide/quick-guide.interface';
import { OrganizationModel } from '../../../organizations/organization.interface';
import { PrintOptionsComponent } from './print-options/print-options.component';
import { FeedbackComponent } from '../feedback/feedback.component';
import { FeedbackService } from '../feedback/feedback.service';
import { ReportManagerComponent } from './report-manager/report-manager.component';
import { ReportPrintModel } from './report-manager/report-manager.interface';
import { PrintReportComponent } from './report-manager/print-report/print-report.component';
import { PrintBipService } from './print-options/print-bip.service';
import { ReportLabelsService } from '../../../manage-settings/report-labels/report-labels.service';
import { LabelKey } from '../../../../core/models/report-labels.model';
import { ReportLabelPipe } from '../../../../core/pipes/report-label.pipe';

interface DataItem {
  title: string;
  instances: string;
  user: string;
  dateCreated: string;
  action: string;
}

@Component({
  selector: 'app-view-bip-case',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzButtonModule,
    NzTableModule,
    CommonModule,
    NzModalModule,
    NzSelectModule,
    NzSwitchModule,
    NzDescriptionsModule,
    BehaviorListComponent,
    NzDropDownModule,
    NoDataComponent,
    NzSpinModule,
    NzBreadCrumbModule,
    ConfirmDialogComponent,
    DrawerHeaderComponent,
    ReportLabelPipe,
  ],
  templateUrl: './view-bip-case.component.html',
  styleUrl: './view-bip-case.component.scss',
})
export class ViewBipCaseComponent implements OnInit, OnDestroy {
  private readonly feedbackService = inject(FeedbackService);
  @ViewChild('drawerTitleTpl') drawerTitleTpl!: TemplateRef<void>;
  @ViewChild('reportMngerTpl') reportMngerTpl!: TemplateRef<void>;
  @ViewChild('addDataTpl') addDataTpl!: TemplateRef<void>;
  @ViewChild('printOptionTpl') printOptionTpl!: TemplateRef<void>;
  @ViewChild('drawerAttachment') drawerAttachment!: TemplateRef<void>;
  @ViewChild('feedbackDataTpl') feedbackDataTpl!: TemplateRef<void>;
  @ViewChild('drawerBehaviorTitleTpl')
  drawerBehaviorTitleTpl!: TemplateRef<void>;
  @ViewChild('faqTpl') drawerFAQTpl!: TemplateRef<void>;
  private readonly destroy$ = new Subject<void>();
  caseDetail: CaseDetailModel | null = null;
  caseParams = {
    case: {} as BIP,
    mode: InsightMode.VIEW,
  };
  loading$: Observable<boolean>;
  deleteStudentConfirmDialog = false;
  printReportData: ReportPrintModel | null = null;
  bipLable: string = LabelKey.BIP;
  constructor(
    private drawerService: DrawerService,
    private insightService: InsightsService,
    private loaderService: LoaderService,
    private viewContainerRef: ViewContainerRef,
    private injector: Injector,
    private commonService: CommonService,
    private cd: ChangeDetectorRef,
    private orgService: OrganizationService,
    private printBipService: PrintBipService,
    private reportLabelsService: ReportLabelsService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
  }

  listOfColumn = [
    {
      title: 'Name',
      compare: (a: DataItem, b: DataItem) => a.title.localeCompare(b.title),
    },
    {
      title: 'Student School',
      compare: (a: DataItem, b: DataItem) =>
        a.instances.localeCompare(b.instances),
    },
    {
      title: 'Student ID',
      compare: (a: DataItem, b: DataItem) => a.user.localeCompare(b.user),
    },
    {
      title: 'Case Owner',
      compare: (a: DataItem, b: DataItem) =>
        a.dateCreated.localeCompare(b.dateCreated),
    },
    { title: '', compare: null },
  ];

  listOfData: DataItem[] = Array.from({ length: 50 }, () => ({
    title: 'Katelyn got out of seat 6 times today',
    instances: '6',
    user: 'Danice Stuart',
    dateCreated: '04/20/2017',
    action: '',
  }));

  isVisible = false;
  showReportConfirmDialog = false;
  isPrintLoading = false;
  organizationDetail?: OrganizationModel;
  isFeedbackSubmitted$ = this.feedbackService.feedbackSubmissionState$;

  async ngOnInit(): Promise<void> {
    await this.getCaseDetail();
    this.reportLabelsService.getLabelValue(LabelKey.BIP).subscribe(value => {
      this.bipLable = value;
    });
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ViewBipCaseDrawerParams>(DRAWER_IDS.BIP_DETAIL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.checkSubmissionStatus(params.case.caseId);
        }
      });
  }

  get reportModelMsg() {
    if (this.printReportData?.isEditMode) {
      return ' Report successfully updated';
    }
    return 'Report successfully created';
  }

  getCaseDetail() {
    this.loaderService.show();
    this.insightService
      .getCaseById(this.caseParams.case.caseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<CaseDetailModel>) => {
          this.caseDetail = response.result;
          if (this.caseDetail && this.caseDetail.caseBehaviors.length > 0) {
            this.caseDetail.caseBehaviors.forEach(behavior => {
              if (behavior.statusId === BehaviorStatus.INACTIVE) {
                behavior.isExpanded = false;
              } else {
                behavior.isExpanded = true;
              }
            });
          }
          if (this.caseDetail && this.caseDetail?.student?.organizationId) {
            this.getOrgById(this.caseDetail.student.organizationId);
          }
          this.loaderService.hide();
        },
        error: error => console.error(error),
      });
  }

  private getOrgById(organizationId: number): void {
    if (!organizationId) return;
    this.loaderService.show();
    this.orgService
      .getOrganization(organizationId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: res => {
          this.organizationDetail = res;
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  showBipModal(): void {
    this.isVisible = true;
  }
  handleOk(): void {
    this.isVisible = false;
  }
  handleCancel(): void {
    this.isVisible = false;
  }
  openInsightsAdminDrawer(): void {
    this.drawerService.openDrawer(
      InsightsAdminComponent,
      this.drawerAttachment as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: InsightMode.VIEW,
      },
      DRAWER_IDS.BIP_STUDENT_ATTACHMENT
    );
  }
  openManageStudentTeamDrawer(): void {
    this.drawerService.openDrawer(
      ManageStudentTeamComponent,
      this.drawerTitleTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: InsightMode.VIEW,
      },
      DRAWER_IDS.BIP_MANGE_STUDENT_TEAM
    );
  }
  closeMangeStudentTeamDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.BIP_MANGE_STUDENT_TEAM);
  }
  closeInsightAdminDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.BIP_STUDENT_ATTACHMENT);
  }
  getFullName(): string {
    return `${this.caseDetail?.student?.firstName || ''} ${this.caseDetail?.student?.lastName || ''}`;
  }
  openEditCaseDrawer(): void {
    this.drawerService.openDrawer(
      CreateBipCaseComponent,
      `${_.startCase(this.caseDetail?.student.firstName)} ${_.startCase(this.caseDetail?.student.lastName)}’s Case`,
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: InsightMode.EDIT,
      },
      DRAWER_IDS.BIP
    );
  }
  openFeedbackDrawer(): void {
    this.drawerService
      .openDrawer(
        FeedbackComponent,
        this.feedbackDataTpl as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          student: this.caseDetail?.student,
          supervisorName: this.caseDetail?.supervisorName,
          caseId: this.caseDetail?.id,
          caseDetail: this.caseDetail,
        },
        DRAWER_IDS.CASE_FEEDBACK
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getCaseDetail();
      });
  }

  closefeedbackFormDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.CASE_FEEDBACK);
  }
  openDefineBehaviorDrawer(): void {
    const behaviorDrawerRef = this.drawerService.openDrawer(
      DefineBehaviorComponent,
      this.drawerBehaviorTitleTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: InsightMode.ADD_BEHAVIOR,
      },
      DRAWER_IDS.DEFINE_BEHAVIOR
    );

    behaviorDrawerRef.afterClose.subscribe(() => {
      this.getCaseDetail();
    });
  }

  openFAQDrawer(): void {
    this.drawerService.openDrawer(
      SupportComponent,
      this.drawerFAQTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {},
      DRAWER_IDS.FAQ
    );
  }
  CloserFAQDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.FAQ);
  }
  openAddDataDrawer(): void {
    const groupedBehaviors = _.groupBy(
      this.caseDetail?.caseBehaviors,
      'behaviorCategoryName'
    );
    this.drawerService.openDrawer(
      AddDataComponent,
      this.addDataTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        groupedBehaviors:
          this.caseDetail?.caseBehaviors &&
          this.caseDetail?.caseBehaviors?.length > 0
            ? groupedBehaviors
            : null,
        mode: InsightMode.VIEW,
      },
      DRAWER_IDS.ADD_DATA
    );
  }
  closeAddDataDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.ADD_DATA);
  }

  openDataLogDrawer() {
    this.drawerService.openDrawer(
      DataLogComponent,
      'Data Log',
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: InsightMode.VIEW,
      },
      DRAWER_IDS.DATA_LOG
    );
  }

  openstudentImageChangeDrawer(): void {
    const studentImageDrawerRef = this.drawerService.openDrawer(
      StudentImageComponent,
      `${_.startCase(this.caseDetail?.student.firstName)} ${_.startCase(this.caseDetail?.student.lastName)}’s Profile Image`,
      480,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: InsightMode.VIEW,
      },
      DRAWER_IDS.STUDENT_PROFILE_IMAGE
    );

    studentImageDrawerRef.afterClose.subscribe(res => {
      if (!!res && res.url && this.caseDetail) {
        this.caseDetail.student.imageUrl = res.url;
        this.cd.detectChanges();
      }
    });
  }

  deleteStudentImage() {
    this.deleteStudentConfirmDialog = true;
  }

  handleDeleteStudent() {
    if (this.caseDetail?.studentId) {
      this.insightService
        .deleteStudentImage(this.caseDetail.studentId)
        .pipe(
          finalize(() => (this.deleteStudentConfirmDialog = false)),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: () => {
            this.commonService.notify(
              'success',
              'Student Image deleted successfully'
            );
            if (this.caseDetail && this.caseDetail.student.imageUrl) {
              this.caseDetail.student.imageUrl = undefined;
            }
            this.deleteStudentConfirmDialog = false;
            this.cd.detectChanges();
          },
          error: ({ errors, message }: ErrorResponse) => {
            const errorMessages = errors.map(
              err => err.description || err.detailedMessage
            );
            this.commonService.notify('error', errorMessages, message);
          },
        });
    }
  }

  handleClose() {
    this.deleteStudentConfirmDialog = false;
  }

  closeBehaviorDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.DEFINE_BEHAVIOR);
  }
  openPrintOptionsDrawer(): void {
    const drawerRef = this.drawerService.openDrawer(
      PrintOptionsComponent,
      this.printOptionTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: InsightMode.PRINT_CASE,
      },
      DRAWER_IDS.BIP_PRINT_OPTIONS
    );

    drawerRef.afterClose.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.caseDetail) {
        this.loaderService.show();
        this.insightService
          .getCaseById(this.caseDetail.id)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.loaderService.hide())
          )
          .subscribe({
            next: (response: ApiResponse<CaseDetailModel>) => {
              this.caseDetail = response.result;
              this.caseDetail.caseBehaviors.forEach(behavior => {
                if (behavior.statusId === BehaviorStatus.INACTIVE) {
                  behavior.isExpanded = false;
                } else {
                  behavior.isExpanded = true;
                }
              });
              this.cd.detectChanges();
            },
            error: ({ errors, message }: ErrorResponse) => {
              const errorMessages = errors?.map(
                err => err.description || err.detailedMessage
              ) || ['Failed to refresh case data'];
              this.commonService.notify('error', errorMessages, message);
            },
          });
      }
    });
  }
  closePrintOptionsDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.BIP_PRINT_OPTIONS);
  }

  openReportManagerDrawer() {
    this.drawerService
      .openDrawer(
        ReportManagerComponent,
        this.reportMngerTpl as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          case: this.caseDetail,
          mode: InsightMode.PRINT_CASE,
        },
        DRAWER_IDS.REPORT_MANAGER
      )
      .afterClose.subscribe(res => {
        if (res) {
          this.showReportConfirmDialog = res.isSuccess;
          this.cd.detectChanges();
          if (res.printData) {
            this.printReportData = res.printData;
          }
        }
      });
  }
  closeReportDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.REPORT_MANAGER);
  }
  printReport() {
    if (this.printReportData && this.printReportData.printData.length > 0) {
      const factory =
        this.viewContainerRef.createComponent(PrintReportComponent);

      if (this.caseDetail) {
        const studentAge = this.caseDetail?.student?.dateOfBirth
          ? this.printBipService.calculateAge(
              this.caseDetail?.student?.dateOfBirth
            )
          : null;
        this.caseDetail.age = studentAge ?? 0;
      }
      const componentElement = factory.location.nativeElement;
      componentElement.style.display = 'none';
      this.isPrintLoading = true;
      factory.instance.reportData = {
        organizationDetail: this.organizationDetail,
        caseData: this.caseDetail,
        printReportData: this.printReportData,
      };
      this.cd.detectChanges();
      this.waitForInitialization(factory).then(async () => {
        await factory.instance.printPDF();
        this.isPrintLoading = false;
        this.showReportConfirmDialog = false;
        this.cd.detectChanges();
        factory.destroy();
      });
    }
  }

  waitForInitialization(factory: ComponentRef<PrintReportComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          this.isPrintLoading = false;
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }
  cancelReport() {
    this.showReportConfirmDialog = false;
  }
  transformBehaviorData(data: BehaviorCase[]): TransformedData[] {
    return data.map(behavior => {
      const title = behavior.behaviorCategoryTypes
        .map(b => b.behaviorTypeName)
        .join(', ');

      const ReactiveStrategy: string[] = [];
      const ProactiveStrategy: string[] = [];
      const ReplacementBehavior: string[] = [];

      if (behavior.strategyResponse && behavior.strategyResponse.length > 0) {
        behavior.strategyResponse.forEach(strategy => {
          if (strategy.strategyTypeName === 'Reactive Strategy') {
            ReactiveStrategy.push(strategy.title);
          } else if (strategy.strategyTypeName === 'Proactive Strategy') {
            ProactiveStrategy.push(strategy.title);
          } else if (strategy.strategyTypeName === 'Replacement Behavior') {
            ReplacementBehavior.push(strategy.title);
          }
        });
      }

      // Determine if all strategy arrays are empty
      const isEmpty =
        ReactiveStrategy.length === 0 &&
        ProactiveStrategy.length === 0 &&
        ReplacementBehavior.length === 0;

      return {
        title,
        ReactiveStrategy,
        ProactiveStrategy,
        ReplacementBehavior,
        isEmpty,
      };
    });
  }

  async printQuickGuide() {
    const factory = this.viewContainerRef.createComponent(
      PrintQuickGuideComponent,
      {
        injector: this.injector,
      }
    );
    const componentElement = factory.location.nativeElement;
    componentElement.style.display = 'none';
    this.isPrintLoading = true;
    if (this.caseDetail && this.caseDetail.student.organizationId) {
      await Promise.all([
        this.commonService.convertImageToBase64('/assets/images/logo.png'),
        this.insightService.getStrategyByCaseId(this.caseDetail.id).toPromise(),
      ])
        .then(
          async ([res, strategyList]: [
            string,
            ApiStratergyResponses | undefined,
          ]) => {
            let base64Image = '';
            if (this.organizationDetail && this.organizationDetail.logoUrl) {
              base64Image = await this.commonService.getBase64ImageFromUrl(
                this.organizationDetail.logoUrl
              );
            } else {
              base64Image = await this.commonService.getBase64ImageFromUrl(
                '/assets/images/logo.png'
              );
            }

            factory.instance.dataquickGuideData = {
              caseDetail: this.caseParams.case,
              logo: res,
              orgDetail: this.organizationDetail,
              orgLogo: base64Image,
              strategyData: strategyList?.result
                ? this.transformBehaviorData(strategyList?.result)
                : [],
            };
            this.cd.detectChanges();
            this.waitForQuickGuideInitialization(factory).then(async () => {
              await factory.instance.printPDF();
              this.commonService.notify(
                'success',
                'Quick guide to plan has been downloaded successfuly.'
              );
              factory.destroy();
              this.isPrintLoading = false;
            });
          }
        )
        .catch(e => console.error(e))
        .finally(() => {
          this.isPrintLoading = false;
          this.cd.markForCheck();
        });
    }
  }

  waitForQuickGuideInitialization(
    factory: ComponentRef<PrintQuickGuideComponent>
  ) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }

  checkSubmissionStatus(caseId: number): void {
    this.feedbackService.checkFeedbackSubmitted(caseId).subscribe();
  }

  hasActiveBehaviors(): boolean {
    if (!this.caseDetail) return false;
    return Boolean(
      this.caseDetail.isOrganizationFeedbackConfigurationEnabled &&
        this.caseDetail.caseBehaviors &&
        this.caseDetail.caseBehaviors.filter(b => !b.draft && b.statusId === 1)
          .length > 0
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
